<template>
  <v-dialog v-if="currentState" v-model="showDialog" persistent scrollable max-width="940px">
    <v-card class="modal-add-folder lf-modal-drop">
      <v-card-title>
        <CustomDialogHeaderComponent :header-title="texts.title"></CustomDialogHeaderComponent>
      </v-card-title>
      <v-card-text classstyle="height: 650px">
        <span v-for="(file, index) in fileList" :key="file.name + index">
          <FileLoadingComponent :file="file" />
        </span>
      </v-card-text>
      <v-card-actions class="modal-footer">
        <div class="right">
          <LfLabelButtonComponent
            v-if="!isLoading"
            class="button"
            :text="texts.buttons.finish"
            :action="onFinnished"
            :variant="LabelButtonVariant.SOLID"
          />
          <LfLabelButtonComponent
            v-else
            class="button"
            :text="texts.buttons.cancel"
            :action="onCancel"
            :variant="LabelButtonVariant.OUTLINED"
          />
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n-composable'
import { UploadSettings } from '@syncfusion/ej2-vue-filemanager'
import { DialogButtonTypes } from '@/store/modules/dialog/dialogTypes'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import { ActionDuplicateFile, FileInfo, FileStatus } from './UploaderTypes'
import CustomDialogHeaderComponent from '@/components/Dialog/CustomDialog/CustomDialogHeaderComponent.vue'
import useUploader from './composables/useUploader'
import useDataTransfer from './composables/useDataTransfer'
import useUploaderDialog from './composables/useUploaderDialog'
import FileLoadingComponent from './components/FileLoadingComponent.vue'
import { createNewFile, getUniqueFileName } from '@/helpers/file'
import useDuplicate from '../../composables/useDuplicate'
import { trackEvent } from '@/plugins/tracker'
import LfLabelButtonComponent from '@/components/forms/buttons/label/LfLabelButtonComponent.vue'

const emit = defineEmits(['droppingFolders', 'droppingFiles'])
const { t } = useI18n()

// DEFINE PROPS  ======================================================================================================
const props = defineProps({
  fileManagerInstance: {
    type: Object,
    required: true
  },
  currentState: {
    type: Object,
    required: true
  },
  uploadSettings: {
    type: Object as PropType<UploadSettings>,
    required: true
  }
})

const texts = {
  title: t('components.file_manager.dialogs.upload_folders_title'),
  errors: {
    extensionNotAllowed: t('components.file_manager.dialogs.extension_not_allowed').toString(),
    maxFileUploadSizeExceeded: t('components.file_manager.dialogs.max_file_upload_size_exceeded', {
      maxFileUploadSize: props.uploadSettings.maxFileSize
    }).toString()
  },
  buttons: {
    cancel: t('action_buttons.cancel').toString(),
    finish: t('action_buttons.accept').toString(),
    saveBothFile: t('action_buttons.save_boths').toString(),
    saveBothFolder: t('action_buttons.save_boths_a').toString(),
    stop: t('action_buttons.stop').toString(),
    replace: t('action_buttons.replace').toString()
  }
}

const showDialog = ref(false)
const duplicatedFiles = ref<FileInfo[]>([])

// COMPOSABLES   ======================================================================================================
const { duplicatedDialog, cancelDialog, getDuplicatedDialogMessage, cantUploapDialog } = useUploaderDialog()
const { fileList, extractFilesFromDataTransfer, clearFiles } = useDataTransfer({
  currentPath: '',
  currentBasePath: props.currentState.cwd?.basePath || ''
})
const { uploadFolder, uploadFile } = useUploader()
const { checkDuplicates } = useDuplicate()

// COMPUTEDS ==========================================================================================================
const currentBasePath = computed(() => props.currentState.cwd.basePath)
const entity = computed(() => ({
  id: props.currentState.cwd.idEntity,
  typeId: props.currentState.cwd.idEntityType,
  additionalTypeId: props.currentState.cwd.idEntityTypeAdditional
}))

const isLoading = computed(() => {
  return fileList.value.some((file) => file.status === FileStatus.LOADING)
})

const elementDropEvent = computed(() => props.fileManagerInstance.detailsviewModule.element)

// EVENTS =============================================================================================================
const allowDrop = (e: any) => {
  e.preventDefault()
}

const onDrop = async (event: any) => {
  event.preventDefault()

  if (!props.currentState.cwd.isDroppable) {
    cantUploapDialog()
    return
  }

  await extractFilesFromDataTransfer(event)

  if (!fileList.value.length) {
    return
  }

  duplicatedFiles.value = checkDuplicates(props.currentState, fileList.value)
  if (duplicatedFiles.value.length === 0) {
    processFiles(ActionDuplicateFile.SAVE)
  } else {
    dispatchDuplicateDialog(duplicatedFiles.value)
  }
}

const onCancel = () => {
  cancelDialog(onFinnished)
}

const onFinnished = async () => {
  await props.fileManagerInstance.refreshFiles()
  stopped()
  attachEvents()
}

// METHODS ============================================================================================================
const dispatchDuplicateDialog = (duplicatedFile: FileInfo[]) => {
  const { isFile } = duplicatedFile[0]

  const textBtn = isFile ? texts.buttons.saveBothFile : texts.buttons.saveBothFolder
  const message = getDuplicatedDialogMessage(duplicatedFile)
  duplicatedDialog(message, [
    {
      text: textBtn,
      variant: LabelButtonVariant.OUTLINED,
      action: () => saveBoths()
    },
    {
      type: DialogButtonTypes.CLOSE,
      text: texts.buttons.stop,
      variant: LabelButtonVariant.OUTLINED,
      action: () => stopped()
    },
    ...(isFile
      ? [
          {
            text: texts.buttons.replace,
            variant: LabelButtonVariant.OUTLINED,
            action: () => replace()
          }
        ]
      : [])
  ])
}

const saveBoths = () => {
  trackEvent('Documentos duplicados', { action: 'Guardar ambos' })
  processFiles(ActionDuplicateFile.SAVE_BOTH)
}

const replace = () => {
  trackEvent('Documentos duplicados', { action: 'Reemplazar' })
  processFiles(ActionDuplicateFile.REPLACE)
}

const attachEvents = () => {
  dettachEvent()
  elementDropEvent.value.addEventListener('drop', onDrop)
  elementDropEvent.value.addEventListener('dragover', allowDrop)
}

const dettachEvent = () => {
  elementDropEvent.value.removeEventListener('drop', onDrop)
  elementDropEvent.value.removeEventListener('dragover', allowDrop)
}

const stopped = () => {
  clearFiles()
  showDialog.value = false
}

// HOOKS =============================================================================================================
onMounted(() => {
  duplicatedFiles.value = []
  attachEvents()
})

onBeforeUnmount(() => {
  dettachEvent()
})

// ===================================================================================================================

async function processFiles(action: ActionDuplicateFile) {
  showDialog.value = true

  for (const file of fileList.value) {
    file.status = FileStatus.LOADING
    const basePath = getBasePath(file)
    const toProcess = createProcessPayload(file, action, basePath)

    if (file.isDirectory) {
      await processDirectory(file, toProcess)
    } else {
      await processFile(file, toProcess)
    }
  }
}

function createProcessPayload(file: any, action: ActionDuplicateFile, basePath: string) {
  return {
    file,
    folder: file,
    mode: getActionFile(file, action),
    basePath,
    entity: entity.value
  }
}

function getActionFile(file: any, action: ActionDuplicateFile): ActionDuplicateFile {
  const actionFile = duplicatedFiles.value.find((f) => f.name === file.name)
  return actionFile ? action : ActionDuplicateFile.SAVE
}

async function processDirectory(file: any, toProcess: any) {
  if (toProcess.mode === ActionDuplicateFile.SAVE_BOTH) {
    prepareDuplicateFolder(file, toProcess)
  }

  const { status, folderId } = await uploadFolder(toProcess)
  file.status = status
  file.folderId = folderId
}

function prepareDuplicateFolder(folder: any, toProcess: any) {
  const folders = props.currentState.files.filter((f: any) => !f.isFile && !f.parent).map((f: any) => f.name) || []
  const filename = getUniqueFileName(folders, folder.name)

  fileList.value.forEach((f) => {
    if (f.parent === folder.name) {
      f.parent = filename
    }
  })

  toProcess.folder.name = filename
}

async function processFile(file: any, toProcess: any) {
  file.message = checkFile(file)
  if (file.message) {
    file.status = FileStatus.ERROR
    return
  }

  if (toProcess.mode === ActionDuplicateFile.SAVE_BOTH && !file.parent) {
    prepareDuplicateFile(file, toProcess)
  }

  if (toProcess.mode === ActionDuplicateFile.REPLACE && !file.parent) {
    toProcess.originalFile = duplicatedFiles.value.find((f) => f.name === file.name)
  }

  file.status = await uploadFile(toProcess)
}

function prepareDuplicateFile(file: any, toProcess: any) {
  const { newFile, filename } = getNewFile(file)
  toProcess.file.name = filename
  toProcess.file.file = newFile
}

function getNewFile(file: FileInfo) {
  const files = props.currentState.files.filter((f: any) => f.isFile && !f.parent).map((f: FileInfo) => f.name)
  const filename = getUniqueFileName(files, file.name)
  const newFile = createNewFile(file.file as File, filename)
  return { newFile, filename }
}

function getBasePath(file: FileInfo): string {
  if (file.parent) {
    const parent = fileList.value.find((p) => p.name === file.parent) as FileInfo
    if (parent.folderId) {
      return `${currentBasePath.value}Folder:${parent.folderId}`
    }
  }
  return file.basePath || ''
}

function checkFile(file: FileInfo): string {
  if (!file) {
    return ''
  }

  const { size } = (file.file as any) || 0
  if (size > props.uploadSettings.maxFileSize) {
    return texts.errors.maxFileUploadSizeExceeded
  }

  const extension = file.name.split('.').pop()?.toLocaleLowerCase() || ''
  if (!props.uploadSettings.allowedExtensions.includes(extension)) {
    return texts.errors.extensionNotAllowed
  }

  return ''
}
</script>
<style lang="scss">
@include file-icons;
</style>

<style lang="scss" scoped>
@import '~@/styles/partials/components/filemanager/drop/modal';
@import '~@/styles/partials/components/filemanager/drop/grid';
.custom-dialog-header-container {
  margin: 0;
}
</style>
